import { useState } from "react";

import { Formik, Form, FormikErrors, getIn } from "formik";
import { useNavigate } from "react-router-dom";

import { useSignUpMutation } from "@sso/apis/registration";
import { AuthHeader } from "@sso/components/AuthHeader";
import { SubmitLoadingButton } from "@sso/components/SubmitLoadingButton";
import { PathKeys, signUpInitialValues } from "@sso/constant";
import { SignUpType } from "@sso/types";
import { Button } from "@sso/ui/Button";
import { CustomLink } from "@sso/ui/CustomLink";
import { Flex, RowContainer } from "@sso/ui/FlexBox";

import { OrganisationForm } from "./OrganisationForm";
import { PersonalForm } from "./PersonalForm";
import { signUpOrgValidation, signUpPersonalValidation } from "./schema";

export const SignUp = () => {
  const navigate = useNavigate();
  const [organisation, setOrganisation] = useState(false);

  const [signUpUser, { isLoading }] = useSignUpMutation();

  const handleBackButton = () => setOrganisation(false);

  const handleOnSubmit = (values: SignUpType) => {
    const { confirmPassword, ...data } = values;
    signUpUser(data)
      .unwrap()
      .then(() => navigate(PathKeys.SIGN_IN));
  };

  const nextStep = (
    errors: FormikErrors<SignUpType>,
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
  ) => {
    const fieldList = Object.keys(signUpPersonalValidation.fields);
    fieldList.forEach(field => setFieldTouched(field, true, true));
    const hasError = fieldList.some(property => getIn(errors, property) && getIn(errors, property).trim() !== "");

    if (!hasError) {
      setOrganisation(true);
    }
  };

  return (
    <>
      <AuthHeader
        title="Create an Account"
        description={organisation ? "Create your first organisation" : "Sign up with your Email address"}
      />
      <Formik
        initialValues={signUpInitialValues}
        validationSchema={organisation ? signUpOrgValidation : signUpPersonalValidation}
        onSubmit={handleOnSubmit}
      >
        {({ errors, setFieldTouched }) => (
          <Form>
            {organisation ? <OrganisationForm /> : <PersonalForm />}
            <RowContainer>
              <Button variant="text" disabled={!organisation} onClick={handleBackButton}>
                Back
              </Button>
              {!organisation && (
                <Flex position="relative" width="100%" margin="0.25rem 0">
                  <Button
                    onClick={() => nextStep(errors, setFieldTouched)}
                    fullWidth
                    type="button"
                    padding="0.7rem 1rem"
                    fontSize="1.25rem"
                  >
                    Next
                  </Button>
                </Flex>
              )}
              {organisation && <SubmitLoadingButton loading={isLoading} buttonTitle={"Sign Up"} />}
            </RowContainer>
          </Form>
        )}
      </Formik>

      <CustomLink to={PathKeys.SIGN_IN} margin="1.5rem 0 0.5rem 0" fontWeight={500}>
        Already have an account?
      </CustomLink>
    </>
  );
};
